// The audio lab's account of the app's own echo canceller (issue #3708), the parts that need no
// AudioContext: the live readout's line about it, and the medians the periodic stats and every
// double-talk phase record. The engine (audio_lab_engine.ts) and the page call these; the tests run
// them under Vitest.
//
// The canceller runs in the analysis worker when the lab's "own echo canceller" mic option is on, as
// it does on the Learn tab on Android, and every result then carries `echo` (EchoReading in
// pitch/types.ts). Each level is a median over the frames that have one: the echo estimate's over the
// windows where one was subtracted (it is -Infinity where nothing was), the microphone's and the
// residual's over every window the canceller saw, the delay over the windows that had found one.
// Values are rounded, because a recorded double-talk run's sidecar travels in a query string.

import { median } from '../../../../audio/pitch/notes.ts';
import { FrameResult } from '../../../../audio/pitch/types.ts';

export interface EchoSummary {
  micDbfsMedian?: number; // the raw microphone
  echoDbfsMedian?: number; // the echo estimate subtracted
  residualDbfsMedian?: number; // what the detectors read
  delayMsMedian?: number; // the guide's delay into the microphone
  cancelledRate: number; // the share of the frames the canceller cleaned
  cancellerMsMedian?: number; // the canceller's time per frame in the worker
}

// What the canceller made of `frames`; undefined when none of them went through it, so a run with the
// option off records nothing new
export function echoSummary(frames: FrameResult[]): EchoSummary | undefined {
  const seen = frames.filter((frame) => frame.echo);
  if (seen.length === 0) {
    return undefined;
  }
  const finite = (values: number[]) => values.filter((value) => Number.isFinite(value));
  const round = (value: number | undefined, digits: number) =>
    value === undefined ? undefined : Number(value.toFixed(digits));
  const delays = seen.map((frame) => frame.echo!.delayMs).filter((delay) => delay !== undefined);
  const times = seen.map((frame) => frame.cancellerMs).filter((ms) => ms !== undefined);
  return {
    micDbfsMedian: round(median(finite(seen.map((frame) => frame.echo!.micDbfs))), 1),
    echoDbfsMedian: round(median(finite(seen.map((frame) => frame.echo!.echoDbfs))), 1),
    residualDbfsMedian: round(median(finite(seen.map((frame) => frame.echo!.residualDbfs))), 1),
    delayMsMedian: round(median(delays), 0),
    cancelledRate: round(seen.filter((frame) => frame.echo!.cancelled).length / frames.length, 3)!,
    cancellerMsMedian: round(median(times), 2),
  };
}

// The live readout's line about the canceller, for a result that went through it
export function cancellerReadout(result: FrameResult): string | undefined {
  const { echo } = result;
  if (!echo) {
    return undefined;
  }
  const level = (dbfs: number) => (Number.isFinite(dbfs) ? `${dbfs.toFixed(0)}` : '-inf');
  const delay = echo.delayMs === undefined ? 'searching' : `delay ${echo.delayMs.toFixed(0)} ms`;
  const state = echo.converged ? 'converged' : echo.cancelled ? 'converging' : 'not cancelling';
  const time = result.cancellerMs === undefined ? '-' : result.cancellerMs.toFixed(1);
  return `canceller ${delay}, mic ${level(echo.micDbfs)} echo ${level(echo.echoDbfs)} residual ${level(echo.residualDbfs)} dBFS, ${state}, ${time} ms`;
}
